import { computed, ref, watch, type Ref, type WritableComputedRef } from 'vue';

/**
 * Bridges an optional `defineModel` ref with internal state so a component works
 * both controlled (parent binds `v-model:open`) and uncontrolled (parent only
 * sets `defaultOpen`).
 *
 * `defineModel` is a compiler macro and must be called in the component's
 * `<script setup>`; pass its ref in here together with a getter for the default.
 *
 * @example
 * const openModel = defineModel<boolean>('open');
 * const { isOpen, toggle } = useControllableModel(openModel, () => props.defaultOpen);
 */
export function useControllableModel(
  model: Ref<boolean | undefined>,
  getDefault: () => boolean,
): { isOpen: WritableComputedRef<boolean>; toggle: () => void } {
  const internalOpen = ref(getDefault());

  watch(getDefault, (value) => {
    if (model.value === undefined) {
      internalOpen.value = value;
    }
  });

  const isOpen = computed<boolean>({
    get(): boolean {
      return model.value ?? internalOpen.value;
    },
    set(value: boolean): void {
      if (model.value !== undefined) {
        model.value = value;
      } else {
        internalOpen.value = value;
      }
    },
  });

  const toggle = (): void => {
    isOpen.value = !isOpen.value;
  };

  return { isOpen, toggle };
}
